import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import TextLink from "../../../components/TextLink";
import logoPgmWhite from "../../../assets/images/landingPage/logo-pgm-white-mode.svg";
import error404 from "../../../assets/images/error/error404.svg";
import error500 from "../../../assets/images/error/error500.svg";

import style from "./styles.module.scss";

type ErrorCode = "403" | "404" | "500";

const commonKeyI18n = "translations.errorPages.common";

interface Props {
  code?: ErrorCode;
  homeLink?: string;
  embedded?: boolean;
}

export default function ErrorPage({ code: codeProp, homeLink = "/", embedded = false }: Props) {
  const { t } = useTranslation();
  const { errorCode } = useParams<{ errorCode?: ErrorCode }>();
  const requested = codeProp || errorCode;
  const code: ErrorCode = requested === "403" || requested === "404" ? requested : "500";
  const isForbidden = code === "403";
  const isNotFound = code === "404";
  const keyI18n = `translations.errorPages.${code}`;
  const supportEmail = t(`${commonKeyI18n}.support.email`);

  return (
    <div className={`${style.background} ${embedded ? style.embedded : ""}`}>
      <div className={style.text}>
        {!embedded && <img src={logoPgmWhite} alt="Logo PGM" />}
        <p className="pgm-headline-small">
          <span className={style.title}>
            {t(`${commonKeyI18n}.errorLabel`, { code })}
          </span>{" "}
          {t(`${keyI18n}.title`)}
        </p>
        <p className="pgm-headline-small">
          <Trans
            i18nKey={`${keyI18n}.description`}
            components={{ br: <br /> }}
          />
        </p>

        {isForbidden ? (
          <div className={style.details}>
            <span className={`pgm-title-medium ${style.title}`}>
              {t(`${keyI18n}.linksPrompt`)}
            </span>
            <TextLink
              link={homeLink}
              text={homeLink === "/" ? t(`${commonKeyI18n}.links.home`) : t(`${keyI18n}.back`)}
            />
          </div>
        ) : isNotFound ? (
          <div className={style.details}>
            <span className={`pgm-title-medium ${style.title}`}>
              {t(`${keyI18n}.linksPrompt`)}
            </span>
            <TextLink link="/" text={t(`${commonKeyI18n}.links.home`)} />
            <TextLink
              link="/consultar/parcelamentos"
              text={t(`${commonKeyI18n}.links.installments`)}
            />
            <TextLink
              link="/consultar/debitos/em_aberto"
              text={t(`${commonKeyI18n}.links.debts`)}
            />
            <TextLink
              link="/validar-documento"
              text={t(`${commonKeyI18n}.links.signature`)}
            />
            <TextLink link="/duvidas" text={t(`${commonKeyI18n}.links.doubts`)} />
            <TextLink link="/contatos" text={t(`${commonKeyI18n}.links.contacts`)} />
          </div>
        ) : (
          <div className={style.details}>
            <p className="pgm-body-small pgm-body-small--emphasized">
              {t(`${commonKeyI18n}.support.message`)}
            </p>
            <TextLink
              className="pgm-title-medium"
              link={`mailto:${supportEmail}`}
              text={supportEmail}
            />
            <TextLink
              className="pgm-title-medium"
              link="https://api.whatsapp.com/send?phone=5585984192045"
              text={t(`${commonKeyI18n}.support.contact`)}
              target="_blank"
            />
          </div>
        )}
      </div>

      {!isForbidden && (
        <div className={style.illustration}>
          <img src={isNotFound ? error404 : error500} alt="" />
        </div>
      )}
    </div>
  );
}
